import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles, Loader2, Check, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import ShakaVideoTile from "@/components/ShakaVideoTile";

function fmt(sec) {
  if (!Number.isFinite(sec)) return "--:--:--";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.floor((sec - Math.floor(sec)) * 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

// Fake waveform bars for visual
function useWaveform(n = 200, seed = 1) {
  return useMemo(() => {
    let s = seed;
    const arr = [];
    for (let i = 0; i < n; i++) {
      s = (s * 9301 + 49297) % 233280;
      const rnd = s / 233280;
      arr.push(0.15 + rnd * 0.85);
    }
    return arr;
  }, [n, seed]);
}

export default function BreakpointEditor() {
  const { id } = useParams();
  const [asset, setAsset] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(0);
  const timelineRef = useRef(null);
  const waveform = useWaveform(320, id?.charCodeAt(0) || 3);

  useEffect(() => {
    api.get(`/assets/${id}`).then((r) => setAsset(r.data));
  }, [id]);

  const dur = asset?.duration_sec || 1;

  const genBreaks = async (density) => {
    setBusy(true);
    try {
      const r = await api.post(`/ai/breakpoints/${id}`, null, { params: { density } });
      setAsset((a) => ({ ...a, breakpoints: r.data.breakpoints }));
      toast.success(`AI proposed ${r.data.breakpoints.length} breaks`);
    } catch {
      toast.error("Failed");
    }
    setBusy(false);
  };

  const confirmBp = async (bpId) => {
    const bps = asset.breakpoints.map((b) => b.id === bpId ? { ...b, confirmed: !b.confirmed } : b);
    setAsset((a) => ({ ...a, breakpoints: bps }));
    await api.patch(`/assets/${id}`, { breakpoints: bps });
  };

  const delBp = async (bpId) => {
    const bps = asset.breakpoints.filter((b) => b.id !== bpId);
    setAsset((a) => ({ ...a, breakpoints: bps }));
    await api.patch(`/assets/${id}`, { breakpoints: bps });
  };

  const addAtCursor = async () => {
    const t = pos * dur;
    const newBp = { id: crypto.randomUUID(), time_sec: t, label: "Manual break", confidence: 1, ai_generated: false, confirmed: true };
    const bps = [...(asset.breakpoints || []), newBp].sort((a, b) => a.time_sec - b.time_sec);
    setAsset((a) => ({ ...a, breakpoints: bps }));
    await api.patch(`/assets/${id}`, { breakpoints: bps });
  };

  const onTimelineClick = (e) => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setPos(p);
  };

  if (!asset) return <div className="p-6 text-zinc-500">Loading…</div>;

  return (
    <div className="p-4">
      <div className="flex items-center gap-4 mb-4">
        <Link to="/console/assets" data-testid="bp-back">
          <Button variant="outline" size="sm" className="rounded-sm border-zinc-800">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Breakpoint Editor</div>
          <h1 className="font-display font-bold text-xl mt-0.5">{asset.name}</h1>
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={() => genBreaks("low")} disabled={busy} data-testid="ai-breaks-low" className="rounded-sm bg-zinc-800 hover:bg-zinc-700 text-xs">
            <Sparkles className="w-3 h-3 mr-1" /> Sparse
          </Button>
          <Button size="sm" onClick={() => genBreaks("medium")} disabled={busy} data-testid="ai-breaks-medium" className="rounded-sm tenant-btn text-xs">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} AI Medium
          </Button>
          <Button size="sm" onClick={() => genBreaks("high")} disabled={busy} data-testid="ai-breaks-high" className="rounded-sm bg-zinc-800 hover:bg-zinc-700 text-xs">
            <Sparkles className="w-3 h-3 mr-1" /> Dense
          </Button>
        </div>
      </div>

      {/* Video preview area (real HLS via Shaka when a preview stream is available) */}
      <div className="panel mb-4">
        <div className="panel-header">
          <span className="panel-title">Preview</span>
          <span className="font-mono text-[10px] text-zinc-500">
            {fmt(pos * dur)} / {fmt(dur)}
          </span>
        </div>
        <div className="relative aspect-video checkerboard bg-black">
          <ShakaVideoTile
            url={asset.preview_hls_url || "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8"}
            brandName={asset.name}
            brandColor="#3F3F46"
            muted
            testId={`bp-preview-${asset.id}`}
          />
          <div className="absolute top-2 left-2 font-mono text-[10px] text-emerald-400 bg-black/70 px-1.5 py-0.5 border border-zinc-700 z-10">
            {fmt(pos * dur)}
          </div>
          <div className="absolute top-2 right-2 font-mono text-[10px] text-zinc-300 bg-black/70 px-1.5 py-0.5 border border-zinc-700 z-10">
            {asset.resolution} · {asset.codec}
          </div>
        </div>
      </div>

      {/* Waveform + timeline */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-title">Timeline · waveform · breakpoints</span>
          <Button size="sm" onClick={addAtCursor} className="rounded-sm bg-zinc-800 hover:bg-zinc-700 text-xs" data-testid="add-bp-at-cursor">
            <Plus className="w-3 h-3 mr-1" /> Insert at cursor
          </Button>
        </div>

        <div className="p-3">
          {/* Waveform */}
          <div
            ref={timelineRef}
            onClick={onTimelineClick}
            className="relative h-32 bg-zinc-950 border border-zinc-800 rounded-sm cursor-crosshair overflow-hidden"
            data-testid="bp-timeline"
          >
            <div className="absolute inset-0 flex items-end">
              {waveform.map((h, i) => (
                <div key={i} style={{ height: `${h * 100}%`, width: `${100 / waveform.length}%` }} className="bg-emerald-500/40" />
              ))}
            </div>
            {/* SCTE-35 markers */}
            {asset.breakpoints?.map((b) => {
              const left = (b.time_sec / dur) * 100;
              return (
                <div
                  key={b.id}
                  className="absolute top-0 bottom-0"
                  style={{ left: `${left}%` }}
                  data-testid={`bp-marker-${b.id}`}
                >
                  <div className={`w-0.5 h-full ${b.confirmed ? "bg-red-500" : "bg-amber-400"}`} />
                  <div className={`absolute -top-1 -translate-x-1/2 text-[9px] font-mono px-1 py-0.5 rounded-sm ${b.confirmed ? "bg-red-500 text-white" : "bg-amber-500/80 text-black"}`}>
                    {b.confirmed ? "SCTE" : "AI"}
                  </div>
                </div>
              );
            })}
            {/* Cursor */}
            <div className="absolute top-0 bottom-0 w-px bg-white pointer-events-none" style={{ left: `${pos * 100}%` }} />
          </div>

          {/* Time ticks */}
          <div className="mt-1 grid grid-cols-6 text-[9px] font-mono text-zinc-600">
            {[0, 0.2, 0.4, 0.6, 0.8, 1].map((p) => (
              <div key={p} className={p === 0 ? "" : p === 1 ? "text-right" : "text-center"}>
                {fmt(p * dur)}
              </div>
            ))}
          </div>

          {/* Breakpoints table */}
          <div className="mt-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              {asset.breakpoints?.length || 0} breakpoints
            </div>
            <div className="space-y-1">
              {asset.breakpoints?.map((b) => (
                <div key={b.id} className="flex items-center gap-3 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm">
                  <span className="font-mono text-xs text-emerald-400 w-24">{fmt(b.time_sec)}</span>
                  <span className="text-xs flex-1">{b.label}</span>
                  <span className="font-mono text-[10px] text-zinc-500">{(b.confidence * 100).toFixed(0)}%</span>
                  <span className={`text-[9px] font-mono px-1.5 rounded-sm border ${b.ai_generated ? "border-amber-500/40 text-amber-400" : "border-zinc-700 text-zinc-400"}`}>
                    {b.ai_generated ? "AI" : "MANUAL"}
                  </span>
                  <button
                    onClick={() => confirmBp(b.id)}
                    data-testid={`bp-confirm-${b.id}`}
                    className={`text-[10px] px-2 py-1 rounded-sm ${b.confirmed ? "bg-red-600 text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}
                  >
                    {b.confirmed ? <><Check className="w-3 h-3 inline mr-1" />SCTE</> : "Confirm"}
                  </button>
                  <button
                    onClick={() => delBp(b.id)}
                    data-testid={`bp-del-${b.id}`}
                    className="text-zinc-500 hover:text-red-400"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {(!asset.breakpoints || asset.breakpoints.length === 0) && (
                <div className="text-xs text-zinc-500 italic px-2 py-4 text-center">
                  No breakpoints yet. Run AI detection above.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
